import { useState } from "react";

// The forced-registration gate shown when the server returns `ready_locked`
// (valuation is ready but the visitor isn't registered). Reuses the existing
// Connect passwordless flow: step 1 captures both email and phone and sends a
// code/link to the email
// (POST /_register), step 2 verifies the typed code (POST /auth/connect/code),
// which establishes the session so the parent can re-poll for the figures.
export default function RegistrationGate({ agentName, regError, onRequestCode, onVerifyCode }) {
  const [mode, setMode] = useState("register");
  const [step, setStep] = useState("contact"); // contact | email_code | login_code
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  // Set when a phone sign-in needs its second, emailed code — holds the masked
  // address it went to. The server remembers which address that is; we only
  // display it.
  const [stepUpSentTo, setStepUpSentTo] = useState(null);

  const inputStyle = {
    width: "100%",
    padding: "0.7rem 0.9rem",
    borderRadius: 10,
    border: "1px solid rgba(0,0,0,0.15)",
    fontSize: "1rem",
    boxSizing: "border-box",
  };

  async function sendCode(e) {
    e.preventDefault();
    if (mode === "register" && (!email.trim() || !phone.trim())) return;
    if (mode === "login" && !identifier.trim()) return;
    setBusy(true);
    const ok = await onRequestCode(
      mode === "register"
        ? { email: email.trim(), phone: phone.trim() }
        : { identifier: identifier.trim() }
    );
    setBusy(false);
    // Only ask for a code once one was actually sent; on 429/422 stay put and
    // let the inline regError explain why. Clear any previously typed code —
    // a leftover from an earlier attempt would fail against the fresh token
    // and burn one of its limited attempts.
    if (ok) {
      setCode("");
      setStep(mode === "register" ? "email_code" : "login_code");
    }
  }

  async function submitCode(e) {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    // Once stepping up, the code being typed is the emailed one; the server
    // resolves which address it belongs to, so send the code alone.
    const result = stepUpSentTo
      ? await onVerifyCode({ code: code.trim() })
      : step === "email_code"
        ? await onVerifyCode({ email: email.trim(), code: code.trim() })
        : await onVerifyCode(identifier.includes("@")
          ? { email: identifier.trim(), code: code.trim() }
          : { phone: identifier.trim(), code: code.trim() });
    if (result?.needsEmailConfirmation) {
      setStepUpSentTo(result.sentTo || "the address on your account");
      setCode("");
    }
    setBusy(false);
  }

  return (
    <div style={{ marginTop: "1.25rem", padding: "1.25rem", borderRadius: 14, background: "rgba(255,255,255,0.14)", textAlign: "left" }}>
      <div style={{ fontWeight: 700, fontSize: "1.1rem" }}>Your estimate is ready</div>
      <p style={{ margin: "0.35rem 0 1rem", opacity: 0.9 }}>
        {mode === "register"
          ? `Verify your email to see your full home value report${agentName ? ` from ${agentName}` : ""}.`
          : "Sign in with your email address, or the mobile number you registered with."}
      </p>

      {step === "contact" ? (
        <form onSubmit={sendCode}>
          {mode === "register" ? (
            <>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                aria-label="Email address"
                required
                style={inputStyle}
              />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Mobile phone"
                aria-label="Mobile phone"
                autoComplete="tel"
                required
                style={{ ...inputStyle, marginTop: "0.6rem" }}
              />
            </>
          ) : (
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="Email or registered mobile"
              aria-label="Email address or registered mobile number"
              required
              style={inputStyle}
            />
          )}
          {regError && <div style={{ marginTop: "0.5rem", color: "#ffd2d2", fontSize: "0.85rem" }}>{regError}</div>}
          <button type="submit" className="ss-c-btn ss-c-btn--accent" disabled={busy} style={{ marginTop: "0.6rem", width: "100%" }}>
            {busy ? "Sending…" : mode === "register" ? "Start verification" : "Send sign-in code"}
          </button>
          <button
            type="button"
            onClick={() => setMode(mode === "register" ? "login" : "register")}
            style={{ marginTop: "0.5rem", width: "100%", background: "transparent", border: "none", color: "inherit", opacity: 0.85, cursor: "pointer", fontSize: "0.85rem" }}
          >
            {mode === "register" ? "Already registered? Sign in" : "Need an account? Register"}
          </button>
        </form>
      ) : (
        <form onSubmit={submitCode}>
          <p style={{ margin: "0 0 0.6rem", fontSize: "0.9rem", opacity: 0.9 }}>
            {stepUpSentTo ? (
              <>One more step — enter the 6-digit code we emailed to <strong>{stepUpSentTo}</strong>.</>
            ) : step === "email_code" ? (
              <>Enter the 6-digit code we emailed to <strong>{email}</strong> (or tap the link in the email).</>
            ) : (
              <>Enter the 6-digit code sent to <strong>{identifier}</strong>.</>
            )}
          </p>
          {/* Signing in by a number nobody has confirmed by text before takes
              both codes: the text proves the handset, the email proves the
              account. Afterwards the number stands on its own. */}
          {stepUpSentTo && (
            <p style={{ margin: "-0.3rem 0 0.6rem", fontSize: "0.8rem", opacity: 0.8 }}>
              We confirm a new mobile number once. Next time, your number alone will sign you in.
            </p>
          )}
          {/* A text only arrives if the visitor registered with this number, and
              the uniform /_register response gives us no way to tell — so warn on
              every phone submission (identical for everyone, so it reveals
              nothing) rather than leave them waiting on a code that never comes. */}
          {step !== "email_code" && !stepUpSentTo && !identifier.includes("@") && (
            <p style={{ margin: "-0.3rem 0 0.6rem", fontSize: "0.8rem", opacity: 0.8 }}>
              No text? Codes are only texted to a mobile number you registered with
              here — otherwise start over and use your email address.
            </p>
          )}
          <input
            type="text"
            inputMode="numeric"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            aria-label="Verification code"
            required
            style={inputStyle}
          />
          {regError && <div style={{ marginTop: "0.5rem", color: "#ffd2d2", fontSize: "0.85rem" }}>{regError}</div>}
          <button type="submit" className="ss-c-btn ss-c-btn--accent" disabled={busy} style={{ marginTop: "0.6rem", width: "100%" }}>
            {busy ? "Verifying…" : step === "email_code" ? "Verify email & show my report" : "Sign in & show my report"}
          </button>
          <button
            type="button"
            onClick={() => {
              setCode("");
              setStepUpSentTo(null);
              setStep("contact");
            }}
            style={{ marginTop: "0.5rem", width: "100%", background: "transparent", border: "none", color: "inherit", opacity: 0.8, cursor: "pointer", fontSize: "0.85rem" }}
          >
            Start over
          </button>
        </form>
      )}
    </div>
  );
}
